"use client";

import { useCallback, useEffect, useMemo, useRef, useState, type MouseEvent as ReactMouseEvent } from "react";
import Link from "next/link";
import { useRouter, useSearchParams, usePathname } from "next/navigation";
import { FESTIVALS } from "@/lib/festival-weather/festivals";
import type { Genre, Region } from "@/lib/festival-weather/types";
import {
  CAPACITY_BUCKET_LABEL,
  CAPACITY_BUCKET_ORDER,
  GENRE_LABEL,
  GENRE_ORDER,
  MONTH_LABELS,
  REGION_COLOUR,
  REGION_LABEL,
  REGION_ORDER,
  capacityBucket,
  festivalsInWindow,
  monthSpansForWindow,
  weekLinesForWindow,
  peakSeasonRange,
  hasWork,
  type CapacityBucket,
  type FestivalInWindow,
  type MonthSpan,
} from "@/lib/festival-weather/calendar";

type Props = {
  initialYear?: number;
  initialMonth?: number;
  lockedMonth?: boolean;
};

type ViewMode = "year" | "month";

const INDICATOR_ICON: Record<string, string> = {
  sunshine: "☀️",
  mixed: "🌤️",
  wet: "🌧️",
  hot: "🔥",
  cold: "❄️",
  dusty: "💨",
};

const SHORT_MONTH = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];

function todayUtc(): Date {
  const d = new Date();
  return new Date(Date.UTC(d.getUTCFullYear(), d.getUTCMonth(), d.getUTCDate()));
}

function parseMonthParam(param: string | null): { year: number; month: number } | null {
  if (!param) return null;
  const m = param.match(/^(\d{4})-(\d{1,2})$/);
  if (!m) return null;
  const year = parseInt(m[1], 10);
  const month = parseInt(m[2], 10);
  if (!Number.isFinite(year) || month < 1 || month > 12) return null;
  return { year, month };
}

function formatMonthLabel(year: number, month: number): string {
  return `${MONTH_LABELS[month - 1]} ${year}`;
}

function addMonth(year: number, month: number, delta: number): { year: number; month: number } {
  const total = (year * 12) + (month - 1) + delta;
  return { year: Math.floor(total / 12), month: (total % 12) + 1 };
}

function formatInlineRange(start: Date, end: Date, isConfirmed: boolean): string {
  const fmt = (d: Date) =>
    d.toLocaleString("en-GB", { day: "numeric", month: "short", timeZone: "UTC" });
  if (isConfirmed) return `${fmt(start)} to ${fmt(end)}`;
  return `typical: ${fmt(start)} to ${fmt(end)}`;
}

function festivalCountLabel(entries: FestivalInWindow[]): string {
  const count = new Set(entries.map((entry) => entry.festival.slug)).size;
  return `${count} ${count === 1 ? "festival" : "festivals"}`;
}

function packRows(items: FestivalInWindow[]): number[] {
  const sorted = items
    .map((f, i) => ({ f, i }))
    .sort((a, b) => a.f.startPct - b.f.startPct);
  const rowEnds: number[] = [];
  const rows = new Array<number>(items.length);
  for (const { f, i } of sorted) {
    const endPct = f.startPct + f.widthPct;
    let placed = false;
    for (let r = 0; r < rowEnds.length; r++) {
      if (rowEnds[r] <= f.startPct + 0.3) {
        rows[i] = r;
        rowEnds[r] = endPct;
        placed = true;
        break;
      }
    }
    if (!placed) {
      rows[i] = rowEnds.length;
      rowEnds.push(endPct);
    }
  }
  return rows;
}

export function CalendarClient({ initialYear, initialMonth, lockedMonth = false }: Props) {
  const router = useRouter();
  const pathname = usePathname();
  const searchParams = useSearchParams();

  const today = useMemo(() => todayUtc(), []);
  const defaultYear = initialYear ?? today.getUTCFullYear();
  const defaultMonth = initialMonth ?? today.getUTCMonth() + 1;

  // View mode: default to year unless URL has month param or lockedMonth
  const hasMonthParam = searchParams.has("month");
  const viewParam = searchParams.get("view");
  const initialView: ViewMode = lockedMonth ? "month" : (viewParam === "month" || hasMonthParam ? "month" : "year");
  const [view, setView] = useState<ViewMode>(initialView);

  const parsedMonth = parseMonthParam(searchParams.get("month"));
  const year = lockedMonth ? defaultYear : (parsedMonth?.year ?? defaultYear);
  const month = lockedMonth ? defaultMonth : (parsedMonth?.month ?? defaultMonth);

  // Year view uses the calendar year
  const [annualYear, setAnnualYear] = useState(today.getUTCFullYear());

  const region = (searchParams.get("region") as Region | "all" | null) ?? "all";
  const genreParam = searchParams.get("genre") ?? "";
  const selectedGenres = useMemo(
    () =>
      new Set(
        genreParam
          .split(",")
          .filter(Boolean)
          .filter((g): g is Genre => (GENRE_ORDER as string[]).includes(g))
      ),
    [genreParam]
  );
  const capacity = (searchParams.get("capacity") as CapacityBucket | "all" | null) ?? "all";
  const work = searchParams.get("work") === "1";
  const queryParam = searchParams.get("q") ?? "";

  const [query, setQuery] = useState(queryParam);
  const [searchOpen, setSearchOpen] = useState(false);
  const [filtersOpen, setFiltersOpen] = useState(false);
  const searchWrapRef = useRef<HTMLDivElement | null>(null);

  const [tooltip, setTooltip] = useState<{
    festival: typeof FESTIVALS[number];
    start: Date;
    end: Date;
    isConfirmed: boolean;
    top: number;
    left: number;
    barWidth: number;
  } | null>(null);

  const showTooltip = useCallback((e: ReactMouseEvent, f: FestivalInWindow) => {
    const rect = e.currentTarget.getBoundingClientRect();
    setTooltip({
      festival: f.festival,
      start: f.start,
      end: f.end,
      isConfirmed: f.isConfirmed,
      top: rect.top,
      left: rect.left + rect.width / 2,
      barWidth: rect.width,
    });
  }, []);

  const hideTooltip = useCallback(() => setTooltip(null), []);

  useEffect(() => {
    setQuery(queryParam);
  }, [queryParam]);

  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (searchWrapRef.current && !searchWrapRef.current.contains(e.target as Node)) {
        setSearchOpen(false);
      }
    };
    window.addEventListener("mousedown", handleClickOutside);
    return () => window.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const updateParams = useCallback(
    (mutate: (params: URLSearchParams) => void) => {
      const params = new URLSearchParams(searchParams.toString());
      mutate(params);
      const qs = params.toString();
      router.replace(qs ? `${pathname}?${qs}` : pathname, { scroll: false });
    },
    [router, pathname, searchParams]
  );

  const switchToMonth = useCallback(
    (y: number, m: number) => {
      setView("month");
      updateParams((p) => {
        p.set("month", `${y}-${String(m).padStart(2, "0")}`);
        p.set("view", "month");
      });
    },
    [updateParams]
  );

  const switchToYear = useCallback(() => {
    setView("year");
    updateParams((p) => {
      p.delete("month");
      p.delete("view");
    });
  }, [updateParams]);

  const setMonth = useCallback(
    (y: number, m: number) => {
      if (lockedMonth) return;
      updateParams((p) => {
        p.set("month", `${y}-${String(m).padStart(2, "0")}`);
      });
    },
    [updateParams, lockedMonth]
  );

  const setRegion = useCallback(
    (r: Region | "all") => {
      updateParams((p) => {
        if (r === "all") p.delete("region");
        else p.set("region", r);
      });
    },
    [updateParams]
  );

  const toggleGenre = useCallback(
    (g: Genre) => {
      updateParams((p) => {
        const next = new Set(selectedGenres);
        if (next.has(g)) next.delete(g);
        else next.add(g);
        if (next.size === 0) p.delete("genre");
        else p.set("genre", [...next].join(","));
      });
    },
    [updateParams, selectedGenres]
  );

  const setCapacity = useCallback(
    (c: CapacityBucket | "all") => {
      updateParams((p) => {
        if (c === "all") p.delete("capacity");
        else p.set("capacity", c);
      });
    },
    [updateParams]
  );

  const toggleWork = useCallback(() => {
    updateParams((p) => {
      if (work) p.delete("work");
      else p.set("work", "1");
    });
  }, [updateParams, work]);

  const setQueryParam = useCallback(
    (q: string) => {
      updateParams((p) => {
        if (!q) p.delete("q");
        else p.set("q", q);
      });
    },
    [updateParams]
  );

  const clearAll = useCallback(() => {
    updateParams((p) => {
      p.delete("region");
      p.delete("genre");
      p.delete("capacity");
      p.delete("work");
      p.delete("q");
    });
    setQuery("");
  }, [updateParams]);

  const anyFilterActive =
    region !== "all" || selectedGenres.size > 0 || capacity !== "all" || work || queryParam !== "";

  // ── Annual view data ──────────────────────────────────────────────────
  const annualFestivals = useMemo(
    () => festivalsInWindow(annualYear, 1, 12),
    [annualYear]
  );
  const annualMonthSpans = useMemo(
    () => monthSpansForWindow(annualYear, 1, 12),
    [annualYear]
  );
  const annualWeekLines = useMemo(
    () => weekLinesForWindow(annualYear, 1, 12),
    [annualYear]
  );
  const annualPeak = useMemo(
    () => peakSeasonRange(annualYear, 1, 12),
    [annualYear]
  );
  const annualTodayPct = useMemo(() => {
    const windowStart = new Date(Date.UTC(annualYear, 0, 1));
    const windowEnd = new Date(Date.UTC(annualYear, 11, 31, 23, 59, 59));
    const span = windowEnd.getTime() - windowStart.getTime();
    const pct = ((today.getTime() - windowStart.getTime()) / span) * 100;
    if (pct < 0 || pct > 100) return null;
    return pct;
  }, [annualYear, today]);

  // ── Month view data ───────────────────────────────────────────────────
  const windowMonths = 3;
  const windowFestivals = useMemo(
    () => festivalsInWindow(year, month, windowMonths),
    [year, month]
  );
  const monthSpans = useMemo(
    () => monthSpansForWindow(year, month, windowMonths),
    [year, month]
  );
  const weekLines = useMemo(
    () => weekLinesForWindow(year, month, windowMonths),
    [year, month]
  );
  const peakRange = useMemo(
    () => peakSeasonRange(year, month, windowMonths),
    [year, month]
  );

  const queryLower = query.trim().toLowerCase();
  const searchResults = useMemo(() => {
    if (queryLower.length < 2) return [];
    return FESTIVALS.filter(
      (f) =>
        f.name.toLowerCase().includes(queryLower) ||
        f.city.toLowerCase().includes(queryLower) ||
        f.country.toLowerCase().includes(queryLower)
    ).slice(0, 6);
  }, [queryLower]);

  const matchesFestival = useCallback(
    (fc: typeof FESTIVALS[number]) => {
      if (region !== "all" && fc.region !== region) return false;
      if (selectedGenres.size > 0) {
        const g = fc.genre ?? [];
        if (!g.some((gg) => selectedGenres.has(gg))) return false;
      }
      if (capacity !== "all") {
        const bucket = capacityBucket(fc.capacity);
        if (bucket !== capacity) return false;
      }
      if (work && !hasWork(fc)) return false;
      if (queryLower.length >= 2) {
        const hay = `${fc.name} ${fc.city} ${fc.country}`.toLowerCase();
        if (!hay.includes(queryLower)) return false;
      }
      return true;
    },
    [region, selectedGenres, capacity, work, queryLower]
  );

  const matchesWindow = useCallback(
    (f: FestivalInWindow) => matchesFestival(f.festival),
    [matchesFestival]
  );

  // Filtered data
  const filteredAnnual = useMemo(
    () => annualFestivals.filter(matchesWindow),
    [annualFestivals, matchesWindow]
  );
  const annualRows = useMemo(() => packRows(filteredAnnual), [filteredAnnual]);
  const annualRowCount = annualRows.length > 0 ? Math.max(...annualRows) + 1 : 0;

  const filteredWindowFestivals = windowFestivals.filter(matchesWindow);

  const prev = addMonth(year, month, -1);
  const next = addMonth(year, month, 1);

  const yearOptions = useMemo(() => {
    const ty = today.getUTCFullYear();
    return [ty, ty + 1];
  }, [today]);

  // Desktop month-view Gantt metrics
  const BAR_HEIGHT = 44;
  const ROW_GAP = 5;
  const ganttHeight = filteredWindowFestivals.length * (BAR_HEIGHT + ROW_GAP) + 16;

  // Annual view metrics
  const ANNUAL_BAR = 30;
  const ANNUAL_GAP = 3;
  const annualHeight = annualRowCount * (ANNUAL_BAR + ANNUAL_GAP) + 12;

  return (
    <div>
      {/* Search */}
      <div className="relative mb-4" ref={searchWrapRef}>
        <input
          type="search"
          value={query}
          onChange={(e) => {
            setQuery(e.target.value);
            setSearchOpen(true);
          }}
          onFocus={() => setSearchOpen(true)}
          onBlur={() => {
            setTimeout(() => setQueryParam(query), 120);
          }}
          placeholder="Search festivals by name, city or country"
          aria-label="Search festivals"
          className="w-full bg-white border border-border-light rounded-full px-5 py-3 text-[14.5px] placeholder:text-text-muted focus:outline-none focus:border-gold/60 focus:ring-2 focus:ring-gold/20"
        />
        {searchOpen && queryLower.length >= 2 && searchResults.length > 0 && (
          <div
            className="absolute left-0 right-0 top-[54px] bg-white border border-border-light rounded-[14px] shadow-[0_10px_30px_rgba(0,0,0,0.08)] z-30 overflow-hidden"
            role="listbox"
          >
            {searchResults.map((f) => (
              <Link
                key={f.slug}
                href={`/festivals/${f.slug}`}
                className="flex items-center gap-3 px-4 py-3 hover:bg-cream no-underline text-charcoal border-b border-border-light/60 last:border-b-0"
                role="option"
              >
                <span className={`w-2.5 h-2.5 rounded-full ${REGION_COLOUR[f.region]} shrink-0`} aria-hidden />
                <div className="min-w-0 flex-1">
                  <div className="font-semibold text-[14px] text-navy truncate">{f.name}</div>
                  <div className="text-[12px] text-text-muted truncate">
                    {f.city}, {f.country} · {REGION_LABEL[f.region]}
                  </div>
                </div>
                <span aria-hidden className="text-gold text-[16px]">→</span>
              </Link>
            ))}
          </div>
        )}
      </div>

      {/* Toolbar: view toggle + navigation + filters */}
      <div className="sticky top-0 z-20 bg-warm-bg/95 backdrop-blur border-b border-border-light pb-3 pt-3 md:pb-4 md:pt-4 mb-4 md:mb-6">
        <div className="flex items-center justify-between gap-2 md:gap-3 mb-3 md:mb-4">
          <div className="flex items-center gap-2">
            {/* View toggle (desktop only) */}
            {!lockedMonth && (
              <div className="hidden md:inline-flex rounded-full border border-border-light bg-white p-0.5 mr-2">
                <button
                  type="button"
                  onClick={switchToYear}
                  className={`rounded-full px-3 py-1.5 text-[12px] font-semibold transition-colors ${view === "year" ? "bg-navy text-white" : "text-navy hover:bg-cream"}`}
                >
                  Year
                </button>
                <button
                  type="button"
                  onClick={() => switchToMonth(year, month)}
                  className={`rounded-full px-3 py-1.5 text-[12px] font-semibold transition-colors ${view === "month" ? "bg-navy text-white" : "text-navy hover:bg-cream"}`}
                >
                  Month
                </button>
              </div>
            )}

            {/* Year selector (desktop: year view only) */}
            {view === "year" && !lockedMonth && (
              <select
                value={annualYear}
                onChange={(e) => setAnnualYear(parseInt(e.target.value, 10))}
                aria-label="Year"
                className="hidden md:block bg-white border border-border-light rounded-full px-3 py-2 text-[13px] font-semibold text-navy focus:outline-none focus:border-gold/60"
              >
                {yearOptions.map((y) => (
                  <option key={y} value={y}>{y}</option>
                ))}
              </select>
            )}

            {/* Year selector (mobile: always visible) */}
            {!lockedMonth && (
              <select
                value={annualYear}
                onChange={(e) => setAnnualYear(parseInt(e.target.value, 10))}
                aria-label="Year"
                className="md:hidden bg-white border border-border-light rounded-full px-3 py-2 text-[13px] font-semibold text-navy focus:outline-none focus:border-gold/60"
              >
                {yearOptions.map((y) => (
                  <option key={y} value={y}>{y}</option>
                ))}
              </select>
            )}

            {/* Month navigation (desktop month view only) */}
            {view === "month" && !lockedMonth && (
              <div className="hidden md:flex items-center gap-2">
                <button
                  type="button"
                  onClick={() => setMonth(prev.year, prev.month)}
                  aria-label="Previous month"
                  className="w-9 h-9 rounded-full border border-border-light bg-white text-navy hover:border-gold/40 flex items-center justify-center"
                >
                  ←
                </button>
                <select
                  value={month}
                  onChange={(e) => setMonth(year, parseInt(e.target.value, 10))}
                  aria-label="Month"
                  className="bg-white border border-border-light rounded-full px-4 py-2 text-[13px] font-semibold text-navy focus:outline-none focus:border-gold/60"
                >
                  {MONTH_LABELS.map((label, i) => (
                    <option key={label} value={i + 1}>{label}</option>
                  ))}
                </select>
                <select
                  value={year}
                  onChange={(e) => setMonth(parseInt(e.target.value, 10), month)}
                  aria-label="Year"
                  className="bg-white border border-border-light rounded-full px-4 py-2 text-[13px] font-semibold text-navy focus:outline-none focus:border-gold/60"
                >
                  {yearOptions.map((y) => (
                    <option key={y} value={y}>{y}</option>
                  ))}
                </select>
                <button
                  type="button"
                  onClick={() => setMonth(next.year, next.month)}
                  aria-label="Next month"
                  className="w-9 h-9 rounded-full border border-border-light bg-white text-navy hover:border-gold/40 flex items-center justify-center"
                >
                  →
                </button>
              </div>
            )}

            {lockedMonth && (
              <div className="font-serif text-[20px] md:text-[26px] font-bold text-navy tracking-[-0.01em]">
                {formatMonthLabel(year, month)}
              </div>
            )}
          </div>

          <button
            type="button"
            onClick={() => setFiltersOpen(true)}
            className="md:hidden inline-flex items-center gap-2 bg-white border border-border-light rounded-full px-4 py-2 text-[13px] font-semibold text-navy"
            aria-label="Open filters"
          >
            Filters
            {anyFilterActive && (
              <span className="inline-block w-1.5 h-1.5 rounded-full bg-gold" aria-hidden />
            )}
          </button>
        </div>

        {/* Desktop filter bar */}
        <div className="hidden md:flex flex-wrap items-center gap-2">
          <FilterPill active={region === "all"} onClick={() => setRegion("all")}>All regions</FilterPill>
          {REGION_ORDER.map((r) => (
            <FilterPill key={r} active={region === r} onClick={() => setRegion(r)}>{REGION_LABEL[r]}</FilterPill>
          ))}
          <span className="mx-2 h-5 w-px bg-border-light" aria-hidden />
          <details className="relative">
            <summary className="list-none cursor-pointer bg-white border border-border-light rounded-full px-4 py-2 text-[13px] font-semibold text-navy hover:border-gold/40">
              Genre
              {selectedGenres.size > 0 && (
                <span className="ml-1.5 inline-flex items-center justify-center w-5 h-5 rounded-full bg-navy text-white text-[10px] font-bold">{selectedGenres.size}</span>
              )}
            </summary>
            <div className="absolute left-0 top-[42px] bg-white border border-border-light rounded-[14px] shadow-[0_10px_30px_rgba(0,0,0,0.08)] p-3 z-20 min-w-[200px]">
              {GENRE_ORDER.map((g) => (
                <label key={g} className="flex items-center gap-2 px-2 py-1.5 rounded-[8px] hover:bg-cream cursor-pointer text-[13px] text-charcoal">
                  <input type="checkbox" checked={selectedGenres.has(g)} onChange={() => toggleGenre(g)} className="accent-navy" />
                  {GENRE_LABEL[g]}
                </label>
              ))}
            </div>
          </details>
          <details className="relative">
            <summary className="list-none cursor-pointer bg-white border border-border-light rounded-full px-4 py-2 text-[13px] font-semibold text-navy hover:border-gold/40">
              Capacity{capacity !== "all" ? `: ${CAPACITY_BUCKET_LABEL[capacity]}` : ""}
            </summary>
            <div className="absolute left-0 top-[42px] bg-white border border-border-light rounded-[14px] shadow-[0_10px_30px_rgba(0,0,0,0.08)] p-2 z-20 min-w-[180px]">
              <button type="button" onClick={() => setCapacity("all")} className={`w-full text-left px-3 py-1.5 rounded-[8px] text-[13px] ${capacity === "all" ? "bg-navy text-white" : "hover:bg-cream text-charcoal"}`}>Any capacity</button>
              {CAPACITY_BUCKET_ORDER.map((c) => (
                <button key={c} type="button" onClick={() => setCapacity(c)} className={`w-full text-left px-3 py-1.5 rounded-[8px] text-[13px] ${capacity === c ? "bg-navy text-white" : "hover:bg-cream text-charcoal"}`}>{CAPACITY_BUCKET_LABEL[c]}</button>
              ))}
            </div>
          </details>
          <FilterPill active={work} onClick={toggleWork}>Work guides</FilterPill>
          {anyFilterActive && (
            <button type="button" onClick={clearAll} className="ml-2 text-[12.5px] font-semibold text-text-muted hover:text-navy underline-offset-4 hover:underline">Clear all</button>
          )}
        </div>
      </div>

      {/* Mobile filter sheet */}
      {filtersOpen && (
        <div className="md:hidden fixed inset-0 z-40 bg-black/40 flex items-end" onClick={() => setFiltersOpen(false)}>
          <div className="bg-white w-full rounded-t-[20px] p-5 max-h-[80vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between mb-4">
              <div className="font-serif text-[20px] font-bold text-navy">Filters</div>
              <button type="button" onClick={() => setFiltersOpen(false)} className="text-text-muted text-[13px] font-semibold" aria-label="Close filters">Done</button>
            </div>
            <FilterGroup label="Region">
              <div className="flex flex-wrap gap-2">
                <FilterPill active={region === "all"} onClick={() => setRegion("all")}>All</FilterPill>
                {REGION_ORDER.map((r) => (
                  <FilterPill key={r} active={region === r} onClick={() => setRegion(r)}>{REGION_LABEL[r]}</FilterPill>
                ))}
              </div>
            </FilterGroup>
            <FilterGroup label="Genre">
              <div className="flex flex-wrap gap-2">
                {GENRE_ORDER.map((g) => (
                  <FilterPill key={g} active={selectedGenres.has(g)} onClick={() => toggleGenre(g)}>{GENRE_LABEL[g]}</FilterPill>
                ))}
              </div>
            </FilterGroup>
            <FilterGroup label="Capacity">
              <div className="flex flex-wrap gap-2">
                <FilterPill active={capacity === "all"} onClick={() => setCapacity("all")}>Any</FilterPill>
                {CAPACITY_BUCKET_ORDER.map((c) => (
                  <FilterPill key={c} active={capacity === c} onClick={() => setCapacity(c)}>{CAPACITY_BUCKET_LABEL[c]}</FilterPill>
                ))}
              </div>
            </FilterGroup>
            <FilterGroup label="Work">
              <FilterPill active={work} onClick={toggleWork}>Work guides</FilterPill>
            </FilterGroup>
            {anyFilterActive && (
              <button type="button" onClick={() => { clearAll(); setFiltersOpen(false); }} className="mt-2 text-[13px] font-semibold text-text-muted hover:text-navy underline-offset-4 hover:underline">Clear all filters</button>
            )}
          </div>
        </div>
      )}

      {/* ═══════════ YEAR VIEW ═══════════ */}
      {view === "year" && (
        <>
          {/* Desktop: horizontal annual timeline (year view) */}
          <div className="hidden md:block">
            {filteredAnnual.length === 0 ? (
              <div className="text-center py-14">
                <div className="font-serif text-[24px] font-bold text-navy mb-2">No festivals match these filters in {annualYear}.</div>
                <p className="text-[14px] text-text-muted mb-5">Try clearing filters or switching year.</p>
                <button type="button" onClick={clearAll} className="border border-border-light bg-white rounded-full px-5 py-2.5 text-[13.5px] font-semibold text-navy">Clear filters</button>
              </div>
            ) : (
              <div className="relative border border-border-light rounded-[16px] bg-white overflow-hidden">
                {/* Month headers */}
                <div className="relative h-9 border-b border-border-light flex">
                  {annualMonthSpans.map((ms) => {
                    const isPeak = ms.month >= 6 && ms.month <= 9;
                    return (
                      <button
                        key={`${ms.year}-${ms.month}`}
                        type="button"
                        onClick={() => switchToMonth(ms.year, ms.month)}
                        className={`flex items-center justify-center text-[10.5px] font-semibold uppercase tracking-[1px] border-r border-border-light/60 last:border-r-0 hover:bg-cream/60 transition-colors ${isPeak ? "bg-gold/[0.04] text-navy" : "text-text-muted"}`}
                        style={{ width: `${ms.widthPct}%` }}
                      >
                        {SHORT_MONTH[ms.month - 1]}
                      </button>
                    );
                  })}
                </div>

                {/* Timeline body */}
                <div className="relative px-2 py-2" style={{ height: `${Math.max(annualHeight, 100)}px` }}>
                  {/* Peak season shading */}
                  {annualPeak && (
                    <div
                      className="absolute top-0 bottom-0 bg-gold/[0.03]"
                      style={{ left: `${annualPeak.startPct}%`, width: `${annualPeak.widthPct}%` }}
                      aria-hidden
                    />
                  )}

                  {/* Week gridlines */}
                  {annualWeekLines.map((pct, i) => (
                    <div key={i} className="absolute top-0 bottom-0 w-px bg-border-light/30" style={{ left: `${pct}%` }} aria-hidden />
                  ))}

                  {/* Month boundary lines */}
                  {annualMonthSpans.slice(1).map((ms) => (
                    <div key={`${ms.year}-${ms.month}`} className="absolute top-0 bottom-0 w-px bg-border-light/70" style={{ left: `${ms.startPct}%` }} aria-hidden />
                  ))}

                  {/* Today marker */}
                  {annualTodayPct !== null && (
                    <div className="absolute top-0 bottom-0 w-[2px] bg-gold z-10" style={{ left: `${annualTodayPct}%` }} aria-hidden>
                      <span className="absolute -top-0.5 -translate-x-1/2 text-[8px] font-bold uppercase tracking-[1px] text-gold bg-white px-1 rounded">
                        Today
                      </span>
                    </div>
                  )}

                  {/* Festival bars */}
                  {filteredAnnual.map((f, i) => {
                    const row = annualRows[i];
                    const top = row * (ANNUAL_BAR + ANNUAL_GAP);
                    return (
                      <Link
                        key={`${f.festival.slug}-${f.year}-${f.start.toISOString()}`}
                        href={`/festivals/${f.festival.slug}`}
                        className={`absolute ${REGION_COLOUR[f.festival.region]} text-white rounded-[6px] px-2 flex items-center text-[10.5px] font-semibold tracking-[-0.01em] hover:brightness-110 hover:shadow-[0_2px_8px_rgba(0,0,0,0.15)] transition-all focus:outline-none focus:ring-2 focus:ring-gold`}
                        style={{
                          top: `${top}px`,
                          left: `${f.startPct}%`,
                          width: `${f.widthPct}%`,
                          height: `${ANNUAL_BAR}px`,
                          minWidth: "20px",
                        }}
                        onMouseEnter={(e) => showTooltip(e, f)}
                        onMouseLeave={hideTooltip}
                      >
                        <span className="truncate">
                          {f.festival.name}
                          <span className="font-normal text-white/60"> · {f.festival.city}</span>
                        </span>
                      </Link>
                    );
                  })}
                </div>

                {/* Footer: region key + count */}
                <div className="px-4 py-3 border-t border-border-light flex flex-wrap items-center gap-4 text-[11px] text-text-muted">
                  {REGION_ORDER.map((r) => (
                    <span key={r} className="inline-flex items-center gap-1.5">
                      <span className={`w-2.5 h-2.5 rounded-full ${REGION_COLOUR[r]}`} aria-hidden />
                      {REGION_LABEL[r]}
                    </span>
                  ))}
                  <span className="ml-auto opacity-70">
                    {festivalCountLabel(filteredAnnual)} · Click a month header to zoom in
                  </span>
                </div>
              </div>
            )}
          </div>

        </>
      )}

      {/* ═══════════ MONTH VIEW (desktop only) ═══════════ */}
      {view === "month" && (
        <>
          <div className="hidden md:block">
            {windowFestivals.length === 0 ? (
              <DesktopEmpty
                monthSpans={monthSpans}
                lockedMonth={lockedMonth}
                onNext={() => setMonth(next.year, next.month)}
                nextLabel={formatMonthLabel(next.year, next.month)}
              />
            ) : filteredWindowFestivals.length === 0 ? (
              <div className="text-center py-14">
                <div className="font-serif text-[24px] font-bold text-navy mb-2">No festivals match these filters.</div>
                <p className="text-[14px] text-text-muted mb-5">Try a different month or reset filters.</p>
                <div className="flex items-center justify-center gap-3">
                  {!lockedMonth && (
                    <button type="button" onClick={() => setMonth(next.year, next.month)} className="bg-navy text-white rounded-full px-5 py-2.5 text-[13.5px] font-semibold">Try {formatMonthLabel(next.year, next.month)}</button>
                  )}
                  <button type="button" onClick={clearAll} className="border border-border-light bg-white rounded-full px-5 py-2.5 text-[13.5px] font-semibold text-navy">Clear filters</button>
                </div>
              </div>
            ) : (
              <div className="relative border border-border-light rounded-[16px] bg-white overflow-hidden">
                <div className="flex">
                  <div className="w-[220px] shrink-0 border-r border-border-light lg:w-[280px]">
                    <div className="flex h-10 items-center border-b border-border-light px-4 text-xs font-semibold text-text-muted">Festival and dates</div>
                    <div className="relative" style={{ height: `${Math.max(ganttHeight, 120)}px` }}>
                      {filteredWindowFestivals.map((entry, i) => (
                        <Link
                          key={`${entry.festival.slug}-${entry.year}-${entry.start.toISOString()}`}
                          href={`/festivals/${entry.festival.slug}`}
                          className="absolute inset-x-0 flex min-h-11 flex-col justify-center px-4 hover:bg-cream focus-visible:outline-2 focus-visible:outline-teal"
                          style={{ top: `${i * (BAR_HEIGHT + ROW_GAP)}px`, height: `${BAR_HEIGHT}px` }}
                        >
                          <span className="truncate text-[13px] font-semibold text-navy">{entry.festival.name}</span>
                          <span className="text-[11px] text-text-muted">{formatInlineRange(entry.start, entry.end, entry.isConfirmed)}</span>
                        </Link>
                      ))}
                    </div>
                  </div>
                  <div className="min-w-0 flex-1">
                {/* Month headers */}
                <div className="relative h-10 border-b border-border-light flex">
                  {monthSpans.map((ms) => {
                    const isPeak = ms.month >= 6 && ms.month <= 9;
                    return (
                      <div
                        key={`${ms.year}-${ms.month}`}
                        className={`flex items-center justify-center text-[12px] font-semibold uppercase tracking-[1.5px] border-r border-border-light last:border-r-0 ${isPeak ? "bg-gold/5 text-navy" : "text-text-muted"}`}
                        style={{ width: `${ms.widthPct}%` }}
                      >
                        {ms.label}
                        {isPeak && <span className="ml-1.5 text-[9px] text-gold font-bold">Peak</span>}
                      </div>
                    );
                  })}
                </div>

                {/* Gantt body */}
                <div className="relative px-3 py-3" style={{ height: `${Math.max(ganttHeight, 120)}px` }}>
                  {peakRange && (
                    <div className="absolute top-0 bottom-0 bg-gold/[0.03]" style={{ left: `${peakRange.startPct}%`, width: `${peakRange.widthPct}%` }} aria-hidden />
                  )}
                  {weekLines.map((pct, i) => (
                    <div key={i} className="absolute top-0 bottom-0 w-px bg-border-light/50" style={{ left: `${pct}%` }} aria-hidden />
                  ))}
                  {monthSpans.slice(1).map((ms) => (
                    <div key={`line-${ms.year}-${ms.month}`} className="absolute top-0 bottom-0 w-px bg-border-light" style={{ left: `${ms.startPct}%` }} aria-hidden />
                  ))}
                  {filteredWindowFestivals.map((f, i) => {
                    const top = i * (BAR_HEIGHT + ROW_GAP);
                    return (
                      <Link
                        key={`${f.festival.slug}-${f.year}-${f.start.toISOString()}`}
                        href={`/festivals/${f.festival.slug}`}
                        aria-label={`${f.festival.name}: ${formatInlineRange(f.start, f.end, f.isConfirmed)}`}
                        className={`absolute ${f.isConfirmed ? REGION_COLOUR[f.festival.region] : "border-2 border-dashed border-navy/30 bg-cream"} rounded-[7px] hover:brightness-110 hover:shadow-[0_2px_10px_rgba(0,0,0,0.15)] transition-all focus:outline-none focus:ring-2 focus:ring-gold`}
                        style={{ top: `${top}px`, left: `${f.startPct}%`, width: `${f.widthPct}%`, height: `${BAR_HEIGHT}px`, minWidth: "44px" }}
                        onMouseEnter={(e) => showTooltip(e, f)}
                        onMouseLeave={hideTooltip}
                      >
                      </Link>
                    );
                  })}
                </div>
                  </div>
                </div>

                <div className="px-4 py-3 border-t border-border-light flex flex-wrap items-center gap-4 text-[11.5px] text-text-muted">
                  {REGION_ORDER.map((r) => (
                    <span key={r} className="inline-flex items-center gap-1.5">
                      <span className={`w-2.5 h-2.5 rounded-full ${REGION_COLOUR[r]}`} aria-hidden />
                      {REGION_LABEL[r]}
                    </span>
                  ))}
                  <span className="ml-auto opacity-70">{festivalCountLabel(filteredWindowFestivals)} shown</span>
                </div>
              </div>
            )}
          </div>

        </>
      )}

      {/* ══════���════ MOBILE: VERTICAL TIMELINE (replaces year + month views) ═══════════ */}
      <div className="md:hidden">
        <MobileVerticalTimeline festivals={filteredAnnual} year={annualYear} today={today} />
      </div>

      {tooltip && (
        <GanttTooltip
          festival={tooltip.festival}
          start={tooltip.start}
          end={tooltip.end}
          isConfirmed={tooltip.isConfirmed}
          top={tooltip.top}
          left={tooltip.left}
        />
      )}
    </div>
  );
}

// ── Subcomponents ────────────────────────────────────────────────────────

function MobileVerticalTimeline({
  festivals,
  year,
  today,
}: {
  festivals: FestivalInWindow[];
  year: number;
  today: Date;
}) {
  const todayRef = useRef<HTMLDivElement>(null);
  const isCurrentYear = year === today.getUTCFullYear();
  const todayMonth = today.getUTCMonth();

  const byMonth = useMemo(() => {
    const groups: FestivalInWindow[][] = Array.from({ length: 12 }, () => []);
    for (const f of festivals) groups[f.start.getUTCFullYear() < year ? 0 : f.start.getUTCMonth()].push(f);
    for (const g of groups) g.sort((a, b) => a.start.getTime() - b.start.getTime());
    return groups;
  }, [festivals, year]);

  useEffect(() => {
    const t = setTimeout(() => todayRef.current?.scrollIntoView({ behavior: "smooth", block: "start" }), 150);
    return () => clearTimeout(t);
  }, []);

  if (festivals.length === 0) {
    return (
      <div className="text-center py-10">
        <div className="font-serif text-[20px] font-bold text-navy mb-2">No festivals match filters.</div>
        <p className="text-[13px] text-text-muted">Try clearing filters to see the full calendar.</p>
      </div>
    );
  }

  const firstFestMonth = byMonth.findIndex((g) => g.length > 0);
  const lastFestMonth = 11 - [...byMonth].reverse().findIndex((g) => g.length > 0);
  const rangeStart = isCurrentYear ? Math.min(firstFestMonth, todayMonth) : firstFestMonth;
  const rangeEnd = isCurrentYear ? Math.max(lastFestMonth, todayMonth) : lastFestMonth;

  return (
    <div className="relative">
      <div className="absolute left-[10px] top-3 bottom-3 w-px bg-border-light" aria-hidden />

      {Array.from({ length: rangeEnd - rangeStart + 1 }, (_, i) => rangeStart + i).map((mi) => {
        const fests = byMonth[mi];
        const isPast = isCurrentYear && mi < todayMonth;
        const isCurrent = isCurrentYear && mi === todayMonth;

        return (
          <div key={mi} ref={isCurrent ? todayRef : undefined}>
            {/* Month header */}
            <div className="flex items-center gap-3 mb-2">
              <div
                className={`w-5 h-5 rounded-full border-2 shrink-0 relative z-10 flex items-center justify-center ${
                  isCurrent
                    ? "bg-gold border-gold"
                    : isPast
                      ? "bg-cream border-border-light/60"
                      : "bg-white border-border-light"
                }`}
              >
                {isCurrent && <div className="w-1.5 h-1.5 rounded-full bg-white" />}
              </div>
              <div className="flex items-center gap-2">
                <span
                  className={`text-[11px] font-semibold uppercase tracking-[2px] ${
                    isCurrent ? "text-navy" : isPast ? "text-text-muted/50" : "text-text-muted"
                  }`}
                >
                  {MONTH_LABELS[mi]}
                </span>
                {isCurrent && (
                  <span className="text-[9px] font-bold uppercase tracking-[1px] text-gold bg-gold/10 px-1.5 py-0.5 rounded-full">
                    Now
                  </span>
                )}
                {fests.length > 0 && (
                  <span className={`text-[10px] ${isPast ? "text-text-muted/40" : "text-text-muted/70"}`}>
                    {fests.length}
                  </span>
                )}
              </div>
            </div>

            {/* Festival cards */}
            {fests.length > 0 ? (
              <div className={`space-y-2.5 mb-5 ${isPast ? "opacity-40" : ""}`}>
                {fests.map((f) => {
                  const isPastFest = !isPast && f.end.getTime() < today.getTime();
                  const daysAway = Math.floor((f.start.getTime() - today.getTime()) / 86_400_000);
                  const showWeather = daysAway >= 0 && daysAway <= 14;
                  return (
                    <div key={`${f.festival.slug}-${f.year}-${f.start.toISOString()}`} className="flex gap-3">
                      <div className="w-5 shrink-0 flex justify-center pt-[17px]">
                        <div className={`w-2 h-2 rounded-full ${REGION_COLOUR[f.festival.region]} relative z-10`} />
                      </div>
                      <Link
                        href={`/festivals/${f.festival.slug}`}
                        className={`flex-1 min-w-0 block bg-white rounded-[12px] border border-border-light p-3.5 no-underline text-charcoal transition-colors hover:border-gold/40 ${isPastFest ? "opacity-40" : ""}`}
                      >
                        <div className="flex items-start justify-between gap-2 mb-1">
                          <div className="font-serif text-[16px] font-bold text-navy leading-[1.2]">
                            {f.festival.name}
                          </div>
                          {showWeather && (
                            <span className="text-[15px] shrink-0 leading-none" aria-hidden>
                              {INDICATOR_ICON[f.festival.indicator] ?? ""}
                            </span>
                          )}
                        </div>
                        <div className="text-[12px] text-text-muted mb-2">
                          {f.festival.city}, {f.festival.country} ·{" "}
                          {formatInlineRange(f.start, f.end, f.isConfirmed)}
                        </div>
                        <div className="flex gap-1.5 flex-wrap">
                          <span className="text-[10px] font-semibold uppercase tracking-[1px] text-text-muted bg-cream/80 px-2 py-0.5 rounded-full">
                            {REGION_LABEL[f.festival.region]}
                          </span>
                          {(f.festival.genre ?? []).slice(0, 2).map((g) => (
                            <span
                              key={g}
                              className="text-[10px] font-semibold uppercase tracking-[1px] text-text-muted bg-cream px-2 py-0.5 rounded-full"
                            >
                              {GENRE_LABEL[g]}
                            </span>
                          ))}
                          {hasWork(f.festival) && (
                            <span className="text-[10px] font-semibold uppercase tracking-[1px] text-gold bg-gold-pale px-2 py-0.5 rounded-full">
                              Work guide
                            </span>
                          )}
                        </div>
                      </Link>
                    </div>
                  );
                })}
              </div>
            ) : (
              <div className="h-4" />
            )}
          </div>
        );
      })}

      {/* Legend */}
      <div className="mt-2 pt-3 border-t border-border-light flex flex-wrap gap-3 text-[10px] text-text-muted">
        {REGION_ORDER.map((r) => (
          <span key={r} className="inline-flex items-center gap-1">
            <span className={`w-2 h-2 rounded-full ${REGION_COLOUR[r]}`} aria-hidden />
            {REGION_LABEL[r]}
          </span>
        ))}
        <span className="ml-auto opacity-70">{festivalCountLabel(festivals)}</span>
      </div>
    </div>
  );
}

function DesktopEmpty({
  monthSpans,
  lockedMonth,
  onNext,
  nextLabel,
}: {
  monthSpans: MonthSpan[];
  lockedMonth: boolean;
  onNext: () => void;
  nextLabel: string;
}) {
  const windowLabel = monthSpans.map((ms) => ms.label).join(" / ");
  return (
    <div className="text-center py-14">
      <div className="font-serif text-[24px] font-bold text-navy mb-2">No festivals in {windowLabel}.</div>
      <p className="text-[14px] text-text-muted mb-5 max-w-[460px] mx-auto">
        Festival season runs May to September across the UK and Europe. Try stepping forward to a summer month.
      </p>
      {!lockedMonth && (
        <button type="button" onClick={onNext} className="bg-navy text-white rounded-full px-5 py-2.5 text-[13.5px] font-semibold">Try {nextLabel}</button>
      )}
    </div>
  );
}

function FilterPill({ active, onClick, children }: { active: boolean; onClick: () => void; children: React.ReactNode }) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={`rounded-full px-4 py-2 text-[13px] font-semibold border transition-colors ${active ? "bg-navy text-white border-navy" : "bg-white text-navy border-border-light hover:border-gold/40"}`}
    >
      {children}
    </button>
  );
}

function FilterGroup({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div className="mb-5">
      <div className="text-[11px] font-semibold uppercase tracking-[1.5px] text-text-muted mb-2">{label}</div>
      {children}
    </div>
  );
}

function GanttTooltip({
  festival,
  start,
  end,
  isConfirmed,
  top,
  left,
}: {
  festival: typeof FESTIVALS[number];
  start: Date;
  end: Date;
  isConfirmed: boolean;
  top: number;
  left: number;
}) {
  const ref = useRef<HTMLDivElement>(null);
  const [nudge, setNudge] = useState(0);

  useEffect(() => {
    if (!ref.current) return;
    const rect = ref.current.getBoundingClientRect();
    const overflow = rect.right - window.innerWidth + 12;
    if (overflow > 0) setNudge(-overflow);
    else if (rect.left < 12) setNudge(12 - rect.left);
    else setNudge(0);
  }, [left]);

  return (
    <div
      ref={ref}
      className="fixed z-50 pointer-events-none bg-white border border-border-light rounded-[12px] shadow-[0_8px_24px_rgba(0,0,0,0.12)] px-3.5 py-3 w-[260px]"
      style={{ top: top - 10, left: left + nudge, transform: "translate(-50%, -100%)" }}
    >
      <div className="font-serif text-[15px] font-bold text-navy leading-snug">{festival.name}</div>
      <div className="text-[12px] text-text-muted mt-0.5">
        {festival.city}, {festival.country} · {formatInlineRange(start, end, isConfirmed)}
      </div>
      {festival.cardOneLiner && (
        <div className="text-[11.5px] text-charcoal leading-[1.4] mt-2">{festival.cardOneLiner}</div>
      )}
      <div className="flex gap-1.5 flex-wrap mt-2">
        {(festival.genre ?? []).slice(0, 3).map((g) => (
          <span key={g} className="text-[9.5px] font-semibold uppercase tracking-[0.5px] text-text-muted bg-cream px-1.5 py-0.5 rounded-full">{GENRE_LABEL[g]}</span>
        ))}
        {festival.capacity != null && (
          <span className="text-[9.5px] font-semibold text-text-muted bg-cream px-1.5 py-0.5 rounded-full">
            {festival.capacity >= 1000 ? `${(festival.capacity / 1000).toFixed(0)}k` : festival.capacity}
          </span>
        )}
      </div>
      <div className="absolute left-1/2 -translate-x-1/2 -bottom-[5px] w-2.5 h-2.5 bg-white border-r border-b border-border-light rotate-45" style={{ marginLeft: -nudge }} aria-hidden />
    </div>
  );
}
